import { ReactNode } from 'react';
import { TooltipProps } from 'recharts';
import styled from 'styled-components';
import {
  ValueType,
  NameType,
  Payload,
} from 'recharts/types/component/DefaultTooltipContent';
import Typography from '../Typography/Typography';
import Shadow from '../Shadow/Shadow';
import Colors from '../Colors';
import { Color } from '..';

const StyledShadow = styled(Shadow)`
  padding: 12px;
  background-color: ${Colors.Common.White};
  border-radius: 8px;
`;

const TooltipLabel = styled(Typography)`
  margin-top: 0;
`;

const TooltipValueContainer = styled.div`
  display: flex;
  align-items: center;

  :not(:last-child) {
    margin-bottom: 5px;
  }
`;

const TooltipValue = styled(Typography)`
  margin: 0 0 0 4px;
`;

const Bar = styled.div<{ color: Color }>`
  display: inline-block;
  width: 4px;
  height: 12px;
  background-color: ${({ color }) => color};
  border-radius: 5px;
`;

const BarChartTooltip = ({
  payload,
  label,
  formatter,
}: TooltipProps<ValueType, NameType>): ReactNode | null => {
  if (!payload || !payload.length) {
    return null;
  }

  const renderValue = (value: ValueType) => {
    if (!value) {
      return 'N/A';
    }

    if (formatter) {
      return formatter(value) as string;
    }

    return Intl.NumberFormat('en-GB', {
      maximumFractionDigits: 2,
    }).format(+value);
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const getBarColor = (item: Payload<ValueType, NameType>) =>
    (item.payload?.color ?? item.color) as Color;

  return (
    <StyledShadow size="lg">
      <TooltipLabel type="text-xs" color={Colors.Gray[500]}>
        {label}
      </TooltipLabel>
      {payload.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <TooltipValueContainer key={i}>
          <Bar color={getBarColor(item)} />
          <TooltipValue type="text-sm" fontWeight="semi-bold">
            {renderValue(item.value ?? '')}
          </TooltipValue>
          <TooltipValue type="text-xs" color={Colors.Gray[500]}>
            {item.name}
          </TooltipValue>
        </TooltipValueContainer>
      ))}
    </StyledShadow>
  );
};

export default BarChartTooltip;
